import { useEffect, useMemo, useRef, type PointerEvent } from "react";

type SwipeToDismissOptions = {
  enabled: boolean;
  onDismiss: () => void;
  threshold?: number;
  velocityThreshold?: number;
};

type SwipeState = {
  pointerId: number;
  startY: number;
  lastY: number;
  lastTime: number;
  velocity: number;
  dragging: boolean;
};

function isCoarseMobilePointer() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse), (max-width: 767px)").matches;
}

export function useSwipeToDismiss<T extends HTMLElement>({
  enabled,
  onDismiss,
  threshold = 120,
  velocityThreshold = 0.7
}: SwipeToDismissOptions) {
  const modalRef = useRef<T | null>(null);
  const stateRef = useRef<SwipeState | null>(null);
  const dismissRef = useRef(onDismiss);

  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!enabled || !modalRef.current) return;
    const node = modalRef.current;
    return () => {
      node.style.transform = "";
      node.style.transition = "";
    };
  }, [enabled]);

  return useMemo(
    () => ({
      modalRef,
      handleProps: {
        onPointerDown: (event: PointerEvent<HTMLElement>) => {
          if (!enabled || !isCoarseMobilePointer() || event.button !== 0) return;
          const node = modalRef.current;
          if (!node) return;
          stateRef.current = {
            pointerId: event.pointerId,
            startY: event.clientY,
            lastY: event.clientY,
            lastTime: performance.now(),
            velocity: 0,
            dragging: false
          };
          node.style.transition = "";
          try {
            event.currentTarget.setPointerCapture?.(event.pointerId);
          } catch {
            // Some synthetic or interrupted pointer streams cannot be captured.
          }
        },
        onPointerMove: (event: PointerEvent<HTMLElement>) => {
          const state = stateRef.current;
          const node = modalRef.current;
          if (!enabled || !state || state.pointerId !== event.pointerId || !node) return;

          const now = performance.now();
          const dy = Math.max(0, event.clientY - state.startY);
          const dt = Math.max(1, now - state.lastTime);
          state.velocity = (event.clientY - state.lastY) / dt;
          state.lastY = event.clientY;
          state.lastTime = now;

          if (dy <= 4) return;
          state.dragging = true;
          event.preventDefault();
          node.style.transform = `translateY(${Math.round(dy)}px)`;
        },
        onPointerUp: (event: PointerEvent<HTMLElement>) => {
          const state = stateRef.current;
          const node = modalRef.current;
          if (!state || state.pointerId !== event.pointerId || !node) return;

          const dy = Math.max(0, event.clientY - state.startY);
          const shouldDismiss = state.dragging && (dy >= threshold || state.velocity >= velocityThreshold);
          stateRef.current = null;
          try {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
          } catch {
            // Pointer capture may already have been released by the browser.
          }

          if (shouldDismiss) {
            node.style.transition = "transform 140ms ease, opacity 140ms ease";
            node.style.transform = "translateY(110vh)";
            window.setTimeout(() => dismissRef.current(), 120);
            return;
          }

          node.style.transition = "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
          node.style.transform = "";
          window.setTimeout(() => {
            if (node.style.transform === "") node.style.transition = "";
          }, 190);
        },
        onPointerCancel: (event: PointerEvent<HTMLElement>) => {
          const state = stateRef.current;
          const node = modalRef.current;
          if (!state || state.pointerId !== event.pointerId || !node) return;
          stateRef.current = null;
          node.style.transition = "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
          node.style.transform = "";
        }
      }
    }),
    [enabled, threshold, velocityThreshold]
  );
}
